import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { useMatch, useMatches } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { type FC } from 'react'
import { toast } from 'sonner'

import { useCasesTableContext } from '@/pages/_app/cases/contexts'
import { CaseStatusActionToastContent } from '@/pages/_app/cases/ui/case-status-action-toast-content/case-status-action-toast-content'
import { CasesTableCell } from '@/pages/_app/ui/cases/cases-table/cases-table-cell/cases-table-cell'
import { CellContentActions } from '@/pages/_app/ui/cases/cases-table/cases-table-cell/cell-content-actions/cell-content-actions'
import { CellContentAssignee } from '@/pages/_app/ui/cases/cases-table/cases-table-cell/cell-content-assignee/cell-content-assignee'
import { CellContentCaseStatus } from '@/pages/_app/ui/cases/cases-table/cases-table-cell/cell-content-case-status-message/cell-content-case-status-message'
import { CellContentCaseNumber } from '@/pages/_app/ui/cases/cases-table/cases-table-cell/cell-content-receipt-number/cell-content-receipt-number'
import { CellContentSelectRow } from '@/pages/_app/ui/cases/cases-table/cases-table-cell/cell-content-select-row/cell-content-select-row'
import { CellContentTags } from '@/pages/_app/ui/cases/cases-table/cases-table-cell/cell-content-tags/cell-content-tags'
import { CaseDeleteDialog } from '@/pages/_app/ui/cases/cases-table/cases-table-row/case-delete-dialog/case-delete-dialog'
import { EditCaseDialog } from '@/pages/_app/ui/cases/dialogs/edit-case-dialog/edit-case-dialog'

import { TableRow } from '@/components/table/table'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useDialog } from '@/lib/hooks/use-dialog'
import type { CaseSearchItem } from '@/lib/queries/business/types'
import { activateCasesMutation } from '@/lib/queries/business/v1/case/active/mutations'
import { archiveCasesMutation } from '@/lib/queries/business/v1/case/archive/mutations'
import { cn } from '@/lib/utils'

import { CellContentApplicant } from '../../cases-table/cases-table-cell/cell-content-applicant/cell-content-applicant'

interface Props {
  aCase: CaseSearchItem
  hideApplicant?: boolean
  selected: boolean
  onToggleCaseRowSelection: (caseId: number) => void
}

export const CasesEoirTableRow: FC<Props> = ({ aCase, hideApplicant = false, selected, onToggleCaseRowSelection }) => {
  const routeMatches = useMatches()
  const isInApplicantCasesTable = routeMatches.some(
    (match) => match.routeId === '/_app/applicants/$applicantId/cases-eoir/$caseNumber'
  )
  const routeMatch = useMatch({
    from: '/_app/applicants/$applicantId/cases-eoir/$caseNumber',
    shouldThrow: false, // Don't throw even if no match
  })

  const { tableType } = useCasesTableContext()

  const { mutateAsync: archiveCases } = useMutation(archiveCasesMutation)
  const { mutateAsync: activateCases } = useMutation(activateCasesMutation)

  const { isDialogOpen: isEditDialogOpen, openDialog: openEditDialog, closeDialog: closeEditDialog } = useDialog()

  const { isDialogOpen: isDeleteDialogOpen, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog()

  const handleChangeCaseStatus = async () => {
    if (tableType === 'active') {
      await archiveCases(
        {
          caseIds: [aCase.case_id],
        },
        {
          onSuccess: async () => {
            toast(
              <CaseStatusActionToastContent caseIds={[aCase.case_id]} changedStatus="inactive" isPlural={false} />,
              {
                duration: 8000,
                className: 'w-fit',
              }
            )
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v4.case.search.index],
            })
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v2.user.index],
            })
          },
        }
      )
    } else if (tableType == 'inactive') {
      await activateCases(
        {
          caseIds: [aCase.case_id],
        },
        {
          onSuccess: async () => {
            toast(<CaseStatusActionToastContent caseIds={[aCase.case_id]} changedStatus="active" isPlural={false} />, {
              duration: 8000,
              className: 'w-fit',
            })
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v4.case.search.index],
            })
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v2.user.index],
            })
          },
        }
      )
    }
  }

  const assignees = aCase.assigneeNames
  const isShowingCaseDetail = isInApplicantCasesTable ? routeMatch?.params?.caseNumber === aCase.case_number : false

  return (
    <>
      <TableRow
        key={aCase.case_id}
        className={cn(
          'hover:bg-transparent hover:outline hover:outline-1 hover:outline-offset-[-1px] hover:outline-black',
          'first-of-type:border-t-[1px]',
          '[&>td]:py-0',
          !isShowingCaseDetail && 'hover:outline hover:outline-1 hover:outline-offset-[-1px] hover:outline-black',
          {
            // Selection highlight
            '!bg-blue-600/5 !outline !outline-1 !outline-offset-[-1px] !outline-[#3B82F6]': isShowingCaseDetail,
          }
        )}
      >
        <CasesTableCell noPadding applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <CellContentSelectRow
            checked={selected}
            onClick={() => {
              onToggleCaseRowSelection(aCase.case_id)
            }}
          />
        </CasesTableCell>

        {!hideApplicant && (
          <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
            <CellContentApplicant name={aCase.applicantName} />
          </CasesTableCell>
        )}

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <CellContentCaseNumber
            caseNumber={aCase.case_number}
            recentlyUpdated={aCase.isUpdated}
            invalid={aCase.is_invalid}
          />
        </CasesTableCell>

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <CellContentTags tags={aCase.tags} />
        </CasesTableCell>

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <CellContentCaseStatus status={aCase.caseStatus} />
        </CasesTableCell>

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <p className={cn('text-sm text-slate-800')}>
            {aCase.updatedAt ? dayjs.utc(aCase.updatedAt).local().format('MM-DD-YYYY') : '-'}
          </p>
        </CasesTableCell>

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <p className={cn('text-sm text-slate-800')}>
            {aCase.nextHearingDate ? dayjs.utc(aCase.nextHearingDate).local().format('MM-DD-YYYY') : '-'}
          </p>
        </CasesTableCell>

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <CellContentAssignee assignees={assignees} />
        </CasesTableCell>

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} eoir>
          <p className={cn('text-sm text-slate-800')}>
            {aCase.applicationFiledDate ? dayjs.utc(aCase.applicationFiledDate).local().format('MM-DD-YYYY') : '-'}
          </p>
        </CasesTableCell>

        <CasesTableCell applicantPathId={aCase.applicant_path_id} caseNumber={aCase.case_number} disabled eoir>
          <CellContentActions
            onClickChangeCaseStatus={handleChangeCaseStatus}
            onClickEdit={openEditDialog}
            onClickDelete={openDeleteDialog}
          />
        </CasesTableCell>
      </TableRow>

      {/* Dialogs */}
      <EditCaseDialog
        open={isEditDialogOpen}
        applicantId={aCase.applicant_path_id.toString()}
        aCase={aCase}
        onClose={closeEditDialog}
      />

      <CaseDeleteDialog
        open={isDeleteDialogOpen}
        caseId={aCase.case_id}
        receiptNumber={aCase.case_number}
        onClose={closeDeleteDialog}
      />
    </>
  )
}
